// useLiveCatalog — scope-entity-backed menu catalog
// ────────────────────────────────────────────────────────────────────────────
// Fetches the digested BFF endpoint GET /items (which now includes
// server-side-joined `section` + `carta` strings) and maps into the
// same `MenuItem` / `MenuSection` shapes the ordering system uses.
//
// Replaces all runtime references to mockMenuCatalog.ts.
// mockMenuCatalog.ts is still used by the MenuImporter admin tool
// as a template seed source.
// ────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react'
import {
  fetchMenuCatalog,
  type CatalogMenuItem,
} from './api/tableOpsApi'
import type { MenuItem, MenuSection } from './mockMenuCatalog'
import type { StationId } from './orderTypes'

const COFFEE_TOKENS = [
  'cafe', 'coffee', 'espresso', 'ristretto', 'americano',
  'cortado', 'cappuccino', 'latte', 'lagrima', 'infusion', 'te',
]

const CASHIER_TOKENS = ['caja', 'cashier', 'checkout', 'cobro', 'cobros']

function norm(v?: string): string {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function normalizeCatalogStation(rawStation: string | undefined, section: string | undefined, name: string | undefined): StationId {
  const station = norm(rawStation)
  const sectionNorm = norm(section)
  const nameNorm = norm(name)

  const looksCashier = CASHIER_TOKENS.some((token) => sectionNorm.includes(token) || station.includes(token) || nameNorm.includes(token))
  if (looksCashier) return 'cashier'

  const looksCafeteria = sectionNorm.includes('cafeteria') || COFFEE_TOKENS.some((token) => nameNorm.includes(token))
  if (looksCafeteria) return 'cafeteria'

  if (station === 'kitchen' || station === 'cashier' || station === 'bar' || station === 'pastry' || station === 'cafeteria') {
    return station
  }

  return 'kitchen'
}

/* ── Types ─────────────────────────────────────────────────────────── */

export interface LiveCatalogState {
  /** All active menu items mapped to the existing MenuItem shape */
  items: MenuItem[]
  /** Unique carta keys (scope_keys) */
  cartas: string[]
  /** Station IDs that have at least one item routed to them */
  activeStations: StationId[]
  /** Loading state */
  loading: boolean
  /** Error message (null = OK) */
  error: string | null
  /** Re-fetch all data */
  refresh: () => void
  /** Group an array of MenuItems by carta|section composite key */
  groupBySection: (items: MenuItem[]) => MenuSection[]
}

/* ── Hook ──────────────────────────────────────────────────────────── */

export function useLiveCatalog(tenantId?: number | string): LiveCatalogState {
  const [rawItems, setRawItems] = useState<CatalogMenuItem[]>([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState<string | null>(null)
  const fetchedRef = useRef(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetchMenuCatalog({ limit: 1000 })
      setRawItems(res.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    // Don't fire until tenant context is resolved — prevents a wasted
    // first request that returns empty items for the caller's own tenant
    // before X-Tenant-Override is applied.
    if (tenantId == null) return
    fetchedRef.current = true
    fetchAll()
  }, [fetchAll, tenantId])

  // Map digested CatalogMenuItem → MenuItem shape used by ordering system
  const items = useMemo<MenuItem[]>(() => {
    return rawItems
      .filter(it => it.active !== false)
      // Drop rows that have no human-readable name OR come from an
      // auto-generated carta artifact (carta keys starting with "menu-ACM"
      // are bootstrap shadow copies and pollute the picker with duplicates).
      .filter(it => {
        const name = (it.name ?? '').trim()
        if (!name) return false
        const cartaKey = String(it.carta ?? '')
        if (/^menu-ACM/i.test(cartaKey)) return false
        return true
      })
      .map((it, idx) => ({
        id:          idx + 1,                              // synthetic numeric ID
        scopeKey:    it.id,                                // scope_key = canonical SKID
        name:        it.name,
        description: it.description,
        price:       it.price,
        section:     it.section,                           // server-side joined name
        carta:       it.carta,                             // server-side joined key
        station:     normalizeCatalogStation(it.station, it.section, it.name),
        prep_s:      it.prep_s ?? 0,                       // reference prep time (seconds)
        active:      it.active !== false,
      }))
  }, [rawItems])

  // Unique carta keys extracted from items
  const cartas = useMemo(() => {
    return Array.from(new Set(items.map(i => i.carta).filter(Boolean)))
  }, [items])

  // Active station IDs (stations that have at least one item)
  const activeStations = useMemo<StationId[]>(() => {
    const set = new Set<StationId>()
    for (const it of items) {
      if (it.station) set.add(it.station)
    }
    return Array.from(set)
  }, [items])

  // Group items by "carta|section" composite key (same logic as mockMenuCatalog)
  const groupBySectionFn = useCallback((menuItems: MenuItem[]): MenuSection[] => {
    const map = new Map<string, MenuItem[]>()
    for (const it of menuItems) {
      const key = `${it.carta}|${it.section}`
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(it)
    }
    return Array.from(map.entries()).map(([key, sectionItems]) => {
      const [carta, title] = key.split('|')
      return { title, carta, items: sectionItems }
    })
  }, [])

  return {
    items,
    cartas,
    activeStations,
    loading,
    error,
    refresh: fetchAll,
    groupBySection: groupBySectionFn,
  }
}

/* ── Context (shared across TableOpsInner + StationKDS) ────────────── */

const LiveCatalogContext = createContext<LiveCatalogState | null>(null)

export { LiveCatalogContext }

export function useLiveCatalogContext(): LiveCatalogState {
  const ctx = useContext(LiveCatalogContext)
  if (!ctx) throw new Error('useLiveCatalogContext must be inside LiveCatalogProvider')
  return ctx
}
